import type { CalendarView } from "@dream/config/constants";
import { parseCalendarView } from "@dream/config/constants";

/**
 * Preferências de calendário persistidas no navegador.
 *
 * Não há API de settings no produto: o kit Ofative grava isso no cliente
 * (tema, início da semana, horários visíveis). Entregas continuam no fuso
 * operacional `America/Fortaleza`.
 */

export type Weekday = 0 | 1 | 2 | 3 | 4 | 5 | 6;

export interface DayHours {
  day: Weekday;
  enabled: boolean;
  from: string;
  to: string;
}

export interface CalendarPreferences {
  weekStartsOn: 0 | 1;
  timeFormat: "12" | "24";
  defaultDurationMin: 15 | 30 | 45 | 60;
  visibleHours: 8 | 12 | 16;
  slotMin: string;
  slotMax: string;
  workingHoursEnabled: boolean;
  workingDays: DayHours[];
  lunchEnabled: boolean;
  lunchAt: string;
  lunchMinutes: 15 | 30 | 45 | 60;
  defaultView: CalendarView;
  colorBy: "type" | "client";
  reduceMotion: boolean;
  pointerCursor: boolean;
  displayName: string;
  firstName: string;
  lastName: string;
  showWeekends: boolean;
  density: "comfortable" | "compact";
  showSearchButton: boolean;
  showAvailabilityButton: boolean;
  showShortcutsButton: boolean;
  showCreateButton: boolean;
  menubarUpcoming: boolean;
  upcomingHorizon: "today" | "week";
  allowDrag: boolean;
  flexibleByDefault: boolean;
  focusGuardEnabled: boolean;
  focusWhen: "morning" | "afternoon";
  minFocusHours: 4 | 8 | 12;
  defaultVideo: "none" | "custom";
  customMeetUrl: string;
  autoJoinMeetings: boolean;
  typeColorOverrides: Record<string, string>;
  mutedTypeIds: string[];
}

export const PREFS_STORAGE_KEY = "dream-calendar-prefs";

export const WEEKDAY_LABELS = [
  "Domingo",
  "Segunda",
  "Terça",
  "Quarta",
  "Quinta",
  "Sexta",
  "Sábado",
] as const;

/** Pastéis da Guideline → Base Colors, na ordem da paleta de color-coding. */
export const COLOR_SWATCHES = [
  "#D0E5FF",
  "#F9DCD3",
  "#DBECDD",
  "#BFDEDD",
  "#E6DBF4",
  "#F9CE7A",
  "#FEEDED",
  "#F99696",
  "#AFD4A5",
  "#E7E7EC",
  "#D5D8DE",
] as const;

export function defaultWorkingDays(): DayHours[] {
  return ([0, 1, 2, 3, 4, 5, 6] as Weekday[]).map((day) => ({
    day,
    enabled: day >= 1 && day <= 5,
    from: "07:00",
    to: "21:00",
  }));
}

export const DEFAULT_PREFERENCES: CalendarPreferences = {
  weekStartsOn: 1,
  timeFormat: "24",
  defaultDurationMin: 30,
  visibleHours: 12,
  slotMin: "07:00",
  slotMax: "21:00",
  workingHoursEnabled: true,
  workingDays: defaultWorkingDays(),
  lunchEnabled: false,
  lunchAt: "12:00",
  lunchMinutes: 30,
  defaultView: "mes",
  colorBy: "type",
  reduceMotion: false,
  pointerCursor: true,
  displayName: "",
  firstName: "",
  lastName: "",
  showWeekends: true,
  density: "comfortable",
  showSearchButton: true,
  showAvailabilityButton: true,
  showShortcutsButton: true,
  showCreateButton: true,
  menubarUpcoming: true,
  upcomingHorizon: "week",
  allowDrag: true,
  flexibleByDefault: true,
  focusGuardEnabled: false,
  focusWhen: "morning",
  minFocusHours: 8,
  defaultVideo: "none",
  customMeetUrl: "",
  autoJoinMeetings: false,
  typeColorOverrides: {},
  mutedTypeIds: [],
};

function asView(value: unknown): CalendarView {
  return parseCalendarView(value);
}

function asTime(value: unknown, fallback: string): string {
  if (typeof value !== "string") return fallback;
  return /^\d{2}:\d{2}$/.test(value) ? value : fallback;
}

function asHex(value: unknown): string | null {
  if (typeof value !== "string") return null;
  return /^#[0-9A-Fa-f]{6}$/.test(value) ? value.toUpperCase() : null;
}

function parseWorkingDays(raw: unknown): DayHours[] {
  const fallback = defaultWorkingDays();
  if (!Array.isArray(raw)) return fallback;
  return fallback.map((day) => {
    const hit = raw.find(
      (item) => item !== null && typeof item === "object" && (item as DayHours).day === day.day,
    ) as DayHours | undefined;
    if (hit === undefined) return day;
    return {
      day: day.day,
      enabled: hit.enabled !== false,
      from: asTime(hit.from, day.from),
      to: asTime(hit.to, day.to),
    };
  });
}

function parseOverrides(raw: unknown): Record<string, string> {
  if (raw === null || typeof raw !== "object" || Array.isArray(raw)) return {};
  const out: Record<string, string> = {};
  for (const [key, value] of Object.entries(raw as Record<string, unknown>)) {
    const hex = asHex(value);
    if (hex !== null && key.length > 0 && key.length < 80) out[key] = hex;
  }
  return out;
}

function splitDisplayName(name: string): { firstName: string; lastName: string } {
  const parts = name
    .trim()
    .split(/\s+/)
    .filter((part) => part.length > 0);
  if (parts.length === 0) return { firstName: "", lastName: "" };
  if (parts.length === 1) return { firstName: parts[0] ?? "", lastName: "" };
  return { firstName: parts[0] ?? "", lastName: parts.slice(1).join(" ") };
}

export function parsePreferences(raw: unknown): CalendarPreferences {
  if (raw === null || typeof raw !== "object") return { ...DEFAULT_PREFERENCES };
  const input = raw as Record<string, unknown>;
  const duration = input.defaultDurationMin;
  const hours = input.visibleHours;
  const lunch = input.lunchMinutes;
  const focus = input.minFocusHours;
  const displayName = typeof input.displayName === "string" ? input.displayName.slice(0, 80) : "";
  const firstFromStore = typeof input.firstName === "string" ? input.firstName.slice(0, 40) : "";
  const lastFromStore = typeof input.lastName === "string" ? input.lastName.slice(0, 40) : "";
  const split = splitDisplayName(displayName);

  return {
    weekStartsOn: input.weekStartsOn === 0 ? 0 : 1,
    timeFormat: input.timeFormat === "12" ? "12" : "24",
    defaultDurationMin:
      duration === 15 || duration === 30 || duration === 45 || duration === 60 ? duration : 30,
    visibleHours: hours === 8 || hours === 12 || hours === 16 ? hours : 12,
    slotMin: asTime(input.slotMin, "07:00"),
    slotMax: asTime(input.slotMax, "21:00"),
    workingHoursEnabled: input.workingHoursEnabled !== false,
    workingDays: parseWorkingDays(input.workingDays),
    lunchEnabled: input.lunchEnabled === true,
    lunchAt: asTime(input.lunchAt, "12:00"),
    lunchMinutes: lunch === 15 || lunch === 30 || lunch === 45 || lunch === 60 ? lunch : 30,
    defaultView: asView(input.defaultView),
    colorBy: input.colorBy === "client" ? "client" : "type",
    reduceMotion: input.reduceMotion === true,
    pointerCursor: input.pointerCursor !== false,
    displayName,
    firstName: firstFromStore !== "" ? firstFromStore : split.firstName,
    lastName: lastFromStore !== "" ? lastFromStore : split.lastName,
    showWeekends: input.showWeekends !== false,
    density: input.density === "compact" ? "compact" : "comfortable",
    showSearchButton: input.showSearchButton !== false,
    showAvailabilityButton: input.showAvailabilityButton !== false,
    showShortcutsButton: input.showShortcutsButton !== false,
    showCreateButton: input.showCreateButton !== false,
    menubarUpcoming: input.menubarUpcoming !== false,
    upcomingHorizon: input.upcomingHorizon === "today" ? "today" : "week",
    allowDrag: input.allowDrag !== false,
    flexibleByDefault: input.flexibleByDefault !== false,
    focusGuardEnabled: input.focusGuardEnabled === true,
    focusWhen: input.focusWhen === "afternoon" ? "afternoon" : "morning",
    minFocusHours: focus === 4 || focus === 8 || focus === 12 ? focus : 8,
    defaultVideo: input.defaultVideo === "custom" ? "custom" : "none",
    customMeetUrl: typeof input.customMeetUrl === "string" ? input.customMeetUrl.slice(0, 500) : "",
    autoJoinMeetings: input.autoJoinMeetings === true,
    typeColorOverrides: parseOverrides(input.typeColorOverrides),
    mutedTypeIds: Array.isArray(input.mutedTypeIds)
      ? input.mutedTypeIds.filter((id): id is string => typeof id === "string").slice(0, 40)
      : [],
  };
}

export function readStoredPreferences(): CalendarPreferences {
  try {
    const raw = localStorage.getItem(PREFS_STORAGE_KEY);
    if (raw === null) return { ...DEFAULT_PREFERENCES };
    return parsePreferences(JSON.parse(raw) as unknown);
  } catch {
    return { ...DEFAULT_PREFERENCES };
  }
}

export function writeStoredPreferences(prefs: CalendarPreferences): void {
  try {
    localStorage.setItem(PREFS_STORAGE_KEY, JSON.stringify(prefs));
  } catch {
    // Storage bloqueado: a sessão ainda aplica os valores em memória.
  }
}

export function applyDocumentPreferences(prefs: CalendarPreferences): void {
  document.documentElement.setAttribute(
    "data-pointer-cursor",
    prefs.pointerCursor ? "true" : "false",
  );
  document.documentElement.setAttribute(
    "data-reduce-motion",
    prefs.reduceMotion ? "true" : "false",
  );
  document.documentElement.setAttribute("data-density", prefs.density);
}

function addMinutes(time: string, minutes: number): string {
  const [h, m] = time.split(":").map(Number);
  const total = Math.min(24 * 60, Math.max(0, (h ?? 0) * 60 + (m ?? 0) + minutes));
  const eh = String(Math.floor(total / 60)).padStart(2, "0");
  const em = String(total % 60).padStart(2, "0");
  return `${eh}:${em}`;
}

export function slotBounds(prefs: CalendarPreferences): { min: string; max: string } {
  if (!prefs.workingHoursEnabled) {
    return { min: "00:00:00", max: "24:00:00" };
  }
  return { min: `${prefs.slotMin}:00`, max: `${prefs.slotMax}:00` };
}

export interface BusinessHourRange {
  daysOfWeek: number[];
  startTime: string;
  endTime: string;
}

/** Horário comercial do FullCalendar a partir dos cards por dia + almoço. */
export function toBusinessHours(prefs: CalendarPreferences): BusinessHourRange[] | boolean {
  if (!prefs.workingHoursEnabled) return false;
  const ranges: BusinessHourRange[] = [];
  for (const day of prefs.workingDays) {
    if (!day.enabled) continue;
    if (prefs.lunchEnabled) {
      const lunchEnd = addMinutes(prefs.lunchAt, prefs.lunchMinutes);
      if (prefs.lunchAt > day.from && lunchEnd < day.to) {
        ranges.push({ daysOfWeek: [day.day], startTime: day.from, endTime: prefs.lunchAt });
        ranges.push({ daysOfWeek: [day.day], startTime: lunchEnd, endTime: day.to });
        continue;
      }
    }
    ranges.push({ daysOfWeek: [day.day], startTime: day.from, endTime: day.to });
  }
  return ranges.length === 0 ? false : ranges;
}

export function focusGuardHours(prefs: CalendarPreferences): BusinessHourRange | null {
  if (!prefs.focusGuardEnabled) return null;
  const weekdays = prefs.workingDays.filter((day) => day.enabled).map((day) => day.day);
  if (weekdays.length === 0) return null;
  return prefs.focusWhen === "afternoon"
    ? { daysOfWeek: weekdays, startTime: "17:00", endTime: "19:00" }
    : { daysOfWeek: weekdays, startTime: "07:00", endTime: "09:00" };
}

export function syncSlotBounds(days: DayHours[]): { slotMin: string; slotMax: string } {
  const on = days.filter((day) => day.enabled);
  if (on.length === 0) return { slotMin: "07:00", slotMax: "21:00" };
  return {
    slotMin: on.reduce((min, day) => (day.from < min ? day.from : min), on[0]?.from ?? "07:00"),
    slotMax: on.reduce((max, day) => (day.to > max ? day.to : max), on[0]?.to ?? "21:00"),
  };
}

export function resolvedTypeColor(
  typeId: string,
  fallback: string,
  prefs: Pick<CalendarPreferences, "typeColorOverrides" | "mutedTypeIds">,
): string {
  if (prefs.mutedTypeIds.includes(typeId)) return "#E7E7EC";
  return prefs.typeColorOverrides[typeId] ?? fallback;
}

const RECENT_KEY = "dream-recent-deliverables";

export interface RecentDeliverable {
  id: string;
  title: string;
  at: number;
}

export function readRecentDeliverables(): RecentDeliverable[] {
  try {
    const raw = localStorage.getItem(RECENT_KEY);
    if (raw === null) return [];
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) return [];
    return parsed
      .filter(
        (item): item is RecentDeliverable =>
          item !== null &&
          typeof item === "object" &&
          typeof (item as RecentDeliverable).id === "string" &&
          typeof (item as RecentDeliverable).title === "string",
      )
      .slice(0, 8);
  } catch {
    return [];
  }
}

export function pushRecentDeliverable(entry: { id: string; title: string }): void {
  const next = [
    { id: entry.id, title: entry.title, at: Date.now() },
    ...readRecentDeliverables().filter((item) => item.id !== entry.id),
  ].slice(0, 8);
  try {
    localStorage.setItem(RECENT_KEY, JSON.stringify(next));
  } catch {
    // ignore
  }
}

export function timeOptions(stepMin = 30): Array<{ value: string; label: string }> {
  const options: Array<{ value: string; label: string }> = [];
  for (let minutes = 0; minutes < 24 * 60; minutes += stepMin) {
    const h = String(Math.floor(minutes / 60)).padStart(2, "0");
    const m = String(minutes % 60).padStart(2, "0");
    const value = `${h}:${m}`;
    options.push({ value, label: value });
  }
  return options;
}
